import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import {
  FETAL_ENGAGEMENT_OPTIONS,
  FETAL_PRESENTATION_OPTIONS,
  FOETAL_HEART_OPTIONS,
  FOETAL_MOVEMENT_OPTIONS,
} from "features/pregnancy/constants/antenatalDropdownOptions";

import style from "./FoetalObservationSection.module.css";

/*
 * Guidance only, never enforced. Presentation used to be hidden below 28 weeks and Heart heard
 * below 20, which is why Darren reported the doppler field as "removed" — it had only vanished on
 * an early visit. His call in review: "just to show them all the time like existing systems and let
 * doctor decide what to fill in when ... we might create a problem by hiding some", with "one of
 * the little ! Pop ups like CDM" carrying the timing instead. The gestation-dependence may well
 * come back later ("In time we can make it dependent") — the thresholds below are the ones the form
 * used to enforce, kept verbatim so the guidance matches the old behaviour.
 */
const PRESENTATION_TIMING_HINT =
  "Normally recorded from 28 weeks' gestation. Shown at every visit, so leave it blank until it applies.";
const HEART_HEARD_TIMING_HINT =
  "Normally recorded from 20 weeks' gestation. Shown at every visit, so leave it blank until it applies.";

export const FoetalObservationSection = ({
  foetusIdentifier,
  values,
  errors,
  onChange,
  formStyle,
  /*
   * A foetus that did not continue (KIN-2075). Its findings are shown, because the visit
   * recorded them and a correction of that visit must not hide them, but they are not offered
   * for editing: whether a correction may amend findings for a baby that has since ended is a
   * clinical question, not one the form should answer by leaving the fields open.
   */
  readOnly = false,
  outcome = null,
}) => {
  const set = (field) => (value) => onChange({ ...values, [field]: value });
  const setInput = (field) => (e) =>
    onChange({ ...values, [field]: e.target.value });

  const fieldError = (field) =>
    errors?.[field]?.message ? (
      <p className={style.fieldError}>{errors[field].message}</p>
    ) : null;

  /*
   * Engagement stays on screen at every gestation. KIN-2042 proposed hiding it below 34 weeks,
   * since that is when it becomes assessable, and Darren answered that it should stay: "it's fine
   * to leave it displayed all the time, I just don't fill it in. My CNS team will prob want to see
   * it displayed." Optional, which was the real problem, not absent. The other three now follow the
   * same rule, so the row is always the same four fields wide.
   */
  const label = foetusIdentifier ? `Baby ${foetusIdentifier}` : "Foetal";

  return (
    <div className={`${style.card} ${readOnly ? style.endedCard : ""}`}>
      {foetusIdentifier && (
        <p className={style.cardTitle}>{label}</p>
      )}
      {!!readOnly && (
        <p className={style.endedNote}>
          This baby is no longer continuing
          {outcome ? ` — ${outcome}` : ""}. What this visit recorded is shown as
          it was recorded, and cannot be changed here.
        </p>
      )}
      <div className={formStyle.inputGroupGrid4Columns} style={{ rowGap: "12px" }}>
        <div>
          <label className={formStyle.pregnancyFormLabel}>
            Presentation
            <InfoTooltip text={PRESENTATION_TIMING_HINT} />
          </label>
          <Select
            options={FETAL_PRESENTATION_OPTIONS}
            value={values.presentation ?? ""}
            onChange={set("presentation")}
            placeholder="Select"
            fullWidth
            disabled={readOnly}
            error={!!errors?.presentation}
          />
          {fieldError("presentation")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Engagement</label>
          <Select
            options={FETAL_ENGAGEMENT_OPTIONS}
            value={values.engagement ?? ""}
            onChange={set("engagement")}
            placeholder="Select"
            fullWidth
            disabled={readOnly}
            error={!!errors?.engagement}
          />
          {fieldError("engagement")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>
            Heart heard (doppler)
            <InfoTooltip text={HEART_HEARD_TIMING_HINT} />
          </label>
          <Select
            options={FOETAL_HEART_OPTIONS}
            value={values.heartHeardWithDoppler ?? ""}
            onChange={set("heartHeardWithDoppler")}
            placeholder="Select"
            fullWidth
            disabled={readOnly}
            error={!!errors?.heartHeardWithDoppler}
          />
          {fieldError("heartHeardWithDoppler")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Activity</label>
          <Select
            options={FOETAL_MOVEMENT_OPTIONS}
            value={values.activity ?? ""}
            onChange={set("activity")}
            placeholder="Select"
            fullWidth
            disabled={readOnly}
            error={!!errors?.activity}
          />
          {fieldError("activity")}
        </div>
        <div className={style.notesField}>
          <label className={formStyle.pregnancyFormLabel}>Notes</label>
          <textarea
            className="form-control"
            placeholder="Optional notes"
            value={values.notes ?? ""}
            onChange={setInput("notes")}
            disabled={readOnly}
            style={{ height: "80px", resize: "vertical" }}
          />
        </div>
      </div>
    </div>
  );
};
